import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Flame, Gift, LayoutGrid, PlayCircle, Sparkles,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../../lib/api';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { useSettings } from '../../context/SettingsContext';
import { useHiddenCategories } from '../../lib/gameCategories';
import { useVisibleHomeCategories } from '../../lib/homeCategories';
import { cacheSliders, readCachedSliders } from '../../lib/sliderCache';
import { slidersAt } from '../../lib/sliderPlacement';
import useSwipe from '../../hooks/useSwipe';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import AuthModal from '../../components/AuthModal';
import ProviderStrip, { providersFromResponse } from '../../components/ProviderStrip';
import ZumrutSection from './ZumrutSection';
import ZumrutGameCard from './ZumrutGameCard';
import { SECTIONS, HOOKS, hook, sectionHook } from '../../lib/domHooks';
import HomeBlocks, { HomeBlock } from '../../components/HomeBlocks';
import BonusHtmlThumb, { bonusHasHtmlArt } from '../../components/BonusHtmlThumb';

const STRIP_LIMIT = 16;
const FEATURE_LIMIT = 8;
const HERO_INTERVAL = 6000;

// Şeritlerin üstündeki kısayol hapları. Adresleri sabit sayfalar olduğu için
// panelden gelmez; kapalı kategoriye gidenler aşağıda elenir.
const SHORTCUTS = [
  { to: '/slotlar', label: 'Sağlayıcılar', icon: LayoutGrid, category: 'slot' },
  { to: '/slotlar?strip=new', label: 'Yeni', icon: Sparkles, category: 'slot' },
  { to: '/slotlar?strip=popular', label: 'Popüler', icon: Flame, category: 'slot' },
  { to: '/canli-casino', label: 'Canlı Casino', icon: PlayCircle, category: 'live-casino' },
  { to: '/promosyonlar', label: 'Bonuslar', icon: Gift },
];

const mapGame = (game) => ({
  id: game.id,
  name: game.name,
  provider: game.provider,
  img: game.image,
  hot: game.hot,
  new: game.new,
  api_provider: game.api_provider,
  game_id: game.game_id,
  demo_supported: !!game.demo_supported,
});

const ZumrutHome = () => {
  const settings = useSettings();
  const gl = useGameLauncher();

  // Klasik vitrinle aynı önbellek: yanıt beklenirken son bannerlar çizilir,
  // taze liste gelince üzerine yazılır (bkz. lib/sliderCache.js).
  const [banners, setBanners] = useState(readCachedSliders);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [live, setLive] = useState([]);
  const [fresh, setFresh] = useState([]);
  const [crash, setCrash] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [heroPage, setHeroPage] = useState(0);
  const hiddenCats = useHiddenCategories();
  const visibleCategories = useVisibleHomeCategories(settings.sports_enabled);

  useEffect(() => {
    const get = (url, params) => client.get(url, { params }).then(({ data }) => data).catch(() => null);

    // Ağ hatasında (`data === null`) önbellekten çizilen bannerlar korunur.
    get('/sliders').then((data) => {
      if (!data) return;
      cacheSliders(data.items || []);
      setBanners(data.items || []);
    });
    // Vitrin şeritleri panelden seçilir (Oyun Yönetimi → Ana Sayfa Vitrini);
    // hiç oyun işaretlenmemişse uç otomatik kaynakla doldurur, şerit boş kalmaz.
    get('/games/showcase', { strip: 'featured', category: 'slot', limit: FEATURE_LIMIT })
      .then((data) => setFeatured(data?.items || []));
    // Üstteki şeritle örtüşenler ayıklanacağı için biraz fazla istenir.
    get('/games/showcase', { strip: 'popular', category: 'slot', limit: STRIP_LIMIT + FEATURE_LIMIT })
      .then((data) => setPopular(data?.items || []));
    get('/games/showcase', { strip: 'new', category: 'slot', limit: STRIP_LIMIT })
      .then((data) => setFresh(data?.items || []));
    get('/games', { category: 'live-casino', limit: STRIP_LIMIT }).then((data) => setLive(data?.items || []));
    get('/games', { category: 'crash', limit: STRIP_LIMIT }).then((data) => setCrash(data?.items || []));
    get('/game-providers', { category: 'slot' }).then((data) => {
      setProviders(providersFromResponse(data));
    });
    get('/bonuses').then((data) => setBonuses((data?.items || data || []).slice(0, 5)));
  }, []);

  // Hero yalnızca görseli çizer (metin/CTA alanı yok); görselsiz banner burada
  // boş bir kutu olurdu, bu yüzden elenir.
  // Yalnızca ana slider banner'ları: 'side' ve katalog sayfası banner'ları
  // buraya düşmemelidir (bkz. lib/sliderPlacement.js).
  const visibleBanners = slidersAt(banners).filter(
    (banner) => (
      banner.image
      && (banner.cta_link !== '/spor' || settings.sports_enabled !== false)
    ),
  );
  const heroCount = Math.max(1, visibleBanners.length);
  const activeHero = heroPage % heroCount;
  const heroBanner = visibleBanners[activeHero];

  // Sayaç heroPage'e de bağlıdır: parmakla kaydıran oyuncu seçtiği banner'ı
  // tam süre görsün, otomatik geçiş kaydırmanın hemen ardına düşmesin.
  useEffect(() => {
    if (heroCount < 2) return undefined;
    const timer = setInterval(() => setHeroPage((page) => (page + 1) % heroCount), HERO_INTERVAL);
    return () => clearInterval(timer);
  }, [heroCount, heroPage]);

  // Mobilde banner parmakla çevrilir; oklar dar ekranda küçülüp kenara sıkışır.
  const heroSwipe = useSwipe({
    enabled: heroCount > 1,
    onNext: () => setHeroPage((page) => (page + 1) % heroCount),
    onPrev: () => setHeroPage((page) => (page - 1 + heroCount) % heroCount),
  });

  const shortcuts = useMemo(
    () => SHORTCUTS.filter((item) => !item.category || !hiddenCats.includes(item.category)),
    [hiddenCats],
  );

  // İki şerit de otomatik dolduğunda aynı oyunları göstermesin.
  const featuredIds = new Set(featured.map((game) => game.id));
  const popularStrip = popular
    .filter((game) => !featuredIds.has(game.id))
    .slice(0, STRIP_LIMIT);

  return (
    <>
      <HomeBlocks theme="zumrut">
        <HomeBlock id={SECTIONS.HERO}>
        {heroBanner && (
          <div className="zumrut-hero-wrap" {...heroSwipe} {...sectionHook(SECTIONS.HERO)}>
            <div className="zumrut-hero" key={activeHero} {...hook(HOOKS.SLIDER)}>
              <Link to={heroBanner.cta_link || '/promosyonlar'} className="zumrut-hero-item">
                <img src={resolveApiAssetUrl(heroBanner.image)} alt={heroBanner.title || 'Kampanya'} />
              </Link>
              {heroCount > 1 && (
                <>
                  <button
                    type="button"
                    className="zumrut-hero-arrow is-prev"
                    onClick={() => setHeroPage((page) => (page - 1 + heroCount) % heroCount)}
                    aria-label="Önceki banner"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    className="zumrut-hero-arrow is-next"
                    onClick={() => setHeroPage((page) => (page + 1) % heroCount)}
                    aria-label="Sonraki banner"
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
            {heroCount > 1 && (
              <div className="zumrut-hero-dots">
                {Array.from({ length: heroCount }, (_, page) => (
                  <button
                    key={page}
                    type="button"
                    className={`zumrut-hero-dot ${page === activeHero ? 'is-active' : ''}`}
                    onClick={() => setHeroPage(page)}
                    aria-label={`${page + 1}. banner`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.CATEGORIES}>
        {visibleCategories.length > 0 && (
          <div className="zumrut-tiles" {...sectionHook(SECTIONS.CATEGORIES)}>
            {visibleCategories.map((category) => (
              <Link
                key={category.id}
                to={category.href || '/'}
                className="zumrut-tile"
                {...hook(HOOKS.CATEGORY_CARD)}
              >
                <span className="zumrut-tile-copy">
                  <span className="zumrut-tile-title block">{category.title}</span>
                  <span className="zumrut-tile-sub block">{category.subtitle}</span>
                  <span className="zumrut-tile-cta">{category.cta_text}</span>
                </span>
                {category.image && (
                  <img
                    className="zumrut-tile-art"
                    src={resolveApiAssetUrl(category.image)}
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </Link>
            ))}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.SHORTCUTS}>
        {shortcuts.length > 0 && (
          <div className="zumrut-chips" {...sectionHook(SECTIONS.SHORTCUTS)}>
            {shortcuts.map((item) => (
              <Link key={item.to} to={item.to} className="zumrut-chip">
                <span className="zumrut-chip-icon"><item.icon size={15} /></span>
                {item.label}
              </Link>
            ))}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PICKS}>
        {featured.length > 0 && (
          <ZumrutSection section={SECTIONS.PICKS} title="Size Özel Seçtiklerimiz">
            {featured.map((game) => (
              <ZumrutGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} variant="feature" />
            ))}
          </ZumrutSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.POPULAR}>
        {popularStrip.length > 0 && (
          <ZumrutSection section={SECTIONS.POPULAR} title="En Popüler" to="/slotlar">
            {popularStrip.map((game) => (
              <ZumrutGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ZumrutSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.LIVE_CASINO}>
        {live.length > 0 && (
          <ZumrutSection section={SECTIONS.LIVE_CASINO} title="Canlı Casino" to="/canli-casino">
            {live.map((game) => (
              <ZumrutGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ZumrutSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.NEW}>
        {fresh.length > 0 && (
          <ZumrutSection section={SECTIONS.NEW} title="Yeni Eklenenler" to="/slotlar">
            {fresh.map((game) => (
              <ZumrutGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ZumrutSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.FAST}>
        {crash.length > 0 && (
          <ZumrutSection section={SECTIONS.FAST} title="Hızlı Oyunlar" to="/crash">
            {crash.map((game) => (
              <ZumrutGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </ZumrutSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PROVIDERS}>
        <ProviderStrip providers={providers} classPrefix="zumrut" />
        </HomeBlock>

        <HomeBlock id={SECTIONS.BONUSES}>
        {bonuses.length > 0 && (
          <ZumrutSection section={SECTIONS.BONUSES} title="Bonuslar" to="/promosyonlar" wide>
            {bonuses.map((bonus, index) => (
              <Link
                key={bonus.id || index}
                to="/promosyonlar"
                className="zumrut-hero"
                style={{ display: 'block' }}
              >
                <span className="zumrut-hero-item" style={{ paddingTop: '56%' }}>
                  {bonus.image ? (
                    <img src={resolveApiAssetUrl(bonus.image)} alt={bonus.title || 'Bonus'} />
                  ) : (
                    <BonusHtmlThumb
                      bonusId={bonus.id}
                      title={bonus.title}
                      enabled={bonusHasHtmlArt(bonus)}
                    >
                      <span
                        style={{
                          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', gap: 6, padding: 16,
                          textAlign: 'center',
                        }}
                      >
                        <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--pusu-gold)' }}>
                          {bonus.amount_text || bonus.title}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--pusu-muted)' }}>
                          {bonus.subtitle || bonus.description}
                        </span>
                      </span>
                    </BonusHtmlThumb>
                  )}
                </span>
              </Link>
            ))}
          </ZumrutSection>
        )}
        </HomeBlock>
      </HomeBlocks>

      <GameChoiceModal
        game={gl.choice}
        user={gl.user}
        balance={gl.balance}
        bonusBalance={gl.bonusBalance}
        demoAvailable={gl.demoAvailable}
        onClose={gl.closeChoice}
        onReal={gl.startReal}
        onDemo={gl.startDemo}
        onLogin={() => gl.openAuth('login')}
        onRegister={() => gl.openAuth('register')}
      />
      {gl.activeGame && (
        <GameModal
          key={`${gl.activeGame.id}-${gl.mode}`}
          game={gl.activeGame}
          mode={gl.mode}
          onClose={gl.closeGame}
        />
      )}
      <AuthModal
        open={!!gl.authTab}
        onClose={() => gl.setAuthTab(null)}
        defaultTab={gl.authTab || 'login'}
      />
    </>
  );
};

export default ZumrutHome;
